import React, { useState, useEffect } from "react";
import { 
  X, 
  Bell, 
  Plus, 
  Trash2, 
  Volume2, 
  Send, 
  CheckCircle2, 
  History, 
  Zap, 
  Target, 
  Clock, 
  Sliders, 
  Shield, 
  Layers, 
  TrendingUp, 
  TrendingDown, 
  Sparkles,
  Info,
  Crown
} from "lucide-react";
import { api } from "../services/api";
import { formatDisplayTime } from "../services/timezoneService";
import { useAuth } from "../contexts/AuthContext";
import { getDailyAlertUsage } from "../services/alertQuotaService";
import { playAlarmSound } from "../services/audioAlarm";

export default function AlertModal({
  isOpen,
  onClose,
  currentSymbol = "XAUUSD",
  currentTimeframe = "M5",
  currentPrice,
  alerts = [],
  alertHistory = [],
  onRefreshAlerts,
  strategyParams = {},
  strategyMode = "hma",
  symbols = [],
  timeframes = ["M1", "M5", "M15", "M30", "H1", "H4", "D1"],
}) {
  const { user, isPro, promptTierGate, setUserProfileModalOpen } = useAuth();
  const [dailyUsage, setDailyUsage] = useState(() => getDailyAlertUsage());
  const [isPlayingTestSound, setIsPlayingTestSound] = useState(false);

  // Alert Creation Form Mode: 'PRICE' or 'STRATEGY'
  const [alertType, setAlertType] = useState("PRICE");

  // Tab 1: Price Alert State
  const [priceSymbol, setPriceSymbol] = useState(currentSymbol);
  const [targetValue, setTargetValue] = useState(currentPrice || "");
  const [priceCondition, setPriceCondition] = useState("PRICE_ABOVE");
  const [priceNote, setPriceNote] = useState("");
  const [priceSoundAlarm, setPriceSoundAlarm] = useState(true);
  const [priceSendTelegram, setPriceSendTelegram] = useState(true);

  // Tab 2: Strategy Signal Alert State
  const [stratSymbol, setStratSymbol] = useState(currentSymbol);
  const [stratTimeframe, setStratTimeframe] = useState(currentTimeframe);
  const [stratName, setStratName] = useState(strategyMode || "hma");
  const [stratDirection, setStratDirection] = useState("ALL"); // "ALL", "BUY", "SELL"
  const [stratNote, setStratNote] = useState("");
  const [stratSoundAlarm, setStratSoundAlarm] = useState(true);
  const [stratSendTelegram, setStratSendTelegram] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [listFilter, setListFilter] = useState("ALL"); // "ALL", "PRICE", "STRATEGY"

  // Sync symbol, price, and daily quota ONLY when modal opens
  useEffect(() => {
    if (isOpen) {
      setDailyUsage(getDailyAlertUsage());
      setPriceSymbol(currentSymbol);
      setStratSymbol(currentSymbol);
      setStratTimeframe(currentTimeframe);
      setStratName(strategyMode || "hma");
      if (currentPrice) {
        setTargetValue(currentPrice);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    setDailyUsage(getDailyAlertUsage());
  }, [alertHistory]);

  if (!isOpen) return null;

  // Handle Play Test Sound (3 repetitions)
  const handleTestAlarm = () => {
    setIsPlayingTestSound(true);
    playAlarmSound("alarm", 3);
    setTimeout(() => setIsPlayingTestSound(false), 2200);
  };

  // Handle Create Price Alert
  const handleCreatePriceAlert = async (e) => {
    e.preventDefault();
    if (!targetValue || isNaN(targetValue)) return;

    setIsSubmitting(true);
    try {
      await api.createAlert({
        alert_type: "PRICE",
        user_id: user?.id,
        telegram_chat_id: user?.telegram_chat_id || "",
        symbol: priceSymbol,
        condition: priceCondition,
        target_value: parseFloat(targetValue),
        note: priceNote,
        sound_alarm: priceSoundAlarm,
        send_telegram: priceSendTelegram,
      });
      setPriceNote("");
      setSuccessMsg(`✅ Đã tạo cảnh báo giá cho ${priceSymbol} (${priceCondition === "PRICE_ABOVE" ? ">=" : "<="} ${targetValue}) thành công!`);
      setTimeout(() => setSuccessMsg(""), 4000);
      if (onRefreshAlerts) onRefreshAlerts();
    } catch (err) {
      console.error("Error creating price alert:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Create Strategy Signal Alert
  const handleCreateStrategyAlert = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      await api.createAlert({
        alert_type: "STRATEGY",
        user_id: user?.id,
        telegram_chat_id: user?.telegram_chat_id || "",
        symbol: stratSymbol,
        timeframe: stratTimeframe,
        strategy: stratName,
        direction: stratDirection,
        strategy_params: strategyParams,
        note: stratNote,
        sound_alarm: stratSoundAlarm,
        send_telegram: stratSendTelegram,
      });
      setStratNote("");
      setSuccessMsg(`✅ Đã tạo cảnh báo tín hiệu ${stratSymbol} (${stratTimeframe}) thành công!`);
      setTimeout(() => setSuccessMsg(""), 4000);
      if (onRefreshAlerts) onRefreshAlerts();
    } catch (err) {
      console.error("Error creating strategy alert:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAlert = async (id) => {
    try {
      await api.deleteAlert(id);
      setSuccessMsg("🗑️ Đã xóa cảnh báo thành công!");
      setTimeout(() => setSuccessMsg(""), 3000);
      if (onRefreshAlerts) onRefreshAlerts();
    } catch (err) {
      console.error("Error deleting alert:", err);
    }
  };

  const getStrategyLabel = (code) => {
    switch (code) {
      case "hma":
        return "HMA Breakout Pro (Zero Lag)";
      case "smc":
        return "SMC Pro (MSB & Liquidity)";
      case "matrix":
        return "Matrix Strategy Pro";
      case "bob_volman":
      case "volman":
        return "Bob Volman Price Action Pro";
      case "liquidity_sweep":
      case "sweep":
      case "sweep_pro":
        return "Liquidity Sweep & MSS Pro";
      case "orb_session":
      case "orb":
        return "ORB Session Breakout Pro";
      default:
        return code?.toUpperCase() || "Hệ Thống";
    }
  };

  // Strictly separate Price Alerts vs Strategy Alerts
  const priceAlerts = alerts.filter(
    (a) => a.alert_type === "PRICE" || (!a.strategy && a.target_value != null)
  );
  const stratAlerts = alerts.filter(
    (a) => a.alert_type === "STRATEGY" || Boolean(a.strategy)
  );

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-slate-800 bg-slate-950/70">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-400 shadow-sm">
              <Bell className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <h2 className="font-bold text-sm sm:text-base text-slate-100 flex items-center gap-2">
                <span>Cài Đặt Chuông Báo &amp; Cảnh Báo Tín Hiệu</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded bg-cyan-950 text-cyan-400 border border-cyan-800 font-mono">
                  Live Alert
                </span>
              </h2>
              <p className="text-[11px] text-slate-400">
                Hú chuông 3 hồi liên tục trên Web &amp; gửi tin nhắn tự động qua Telegram
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleTestAlarm}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer border ${
                isPlayingTestSound
                  ? "bg-amber-500/30 text-amber-300 border-amber-500/50 animate-pulse"
                  : "bg-slate-900 hover:bg-slate-800 text-slate-300 border-slate-700 hover:text-white"
              }`}
              title="Nghe thử chuông báo lặp lại 3 lần"
            >
              <Volume2 className="w-3.5 h-3.5 text-amber-400" />
              <span className="hidden sm:inline">Thử chuông (3 lần)</span>
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5">
          {/* Daily Alert Quota Usage Banner */}
          {isPro ? (
            <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-slate-950 border border-amber-500/30 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold text-xs flex items-center gap-1 shrink-0">
                  <Crown className="w-3.5 h-3.5 text-amber-400" />
                  <span>TÀI KHOẢN PRO</span>
                </div>
                <div>
                  <div className="text-xs text-slate-100 font-bold">
                    Cảnh báo không giới hạn (Realtime Web &amp; Telegram)
                  </div>
                  <div className="text-[11px] text-amber-300/80">
                    Chuông 3 hồi liên tục kèm thông báo Realtime
                  </div>
                </div>
              </div>
              <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-950/60 px-2.5 py-1 rounded-lg border border-emerald-800/60 shrink-0">
                Không Giới Hạn ∞
              </span>
            </div>
          ) : (
            <div className="p-3.5 rounded-xl bg-slate-950/90 border border-slate-800/90 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 shrink-0">
                    <Bell className="w-3.5 h-3.5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-200 flex items-center gap-1.5">
                      <span>Hạn mức nhận cảnh báo hôm nay (Tài khoản Free)</span>
                    </div>
                    <div className="text-[10.5px] text-slate-400">
                      Tài khoản Free tối đa 5 lần kích hoạt/ngày • Chuông 3 lần
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded border ${
                    dailyUsage.count >= 5 
                      ? "bg-rose-950/60 text-rose-400 border-rose-800/60"
                      : dailyUsage.count >= 3
                      ? "bg-amber-950/60 text-amber-300 border-amber-800/60"
                      : "bg-cyan-950/60 text-cyan-300 border-cyan-800/60"
                  }`}>
                    {dailyUsage.count} / 5
                  </span>
                  <button
                    type="button"
                    onClick={() => promptTierGate("Nhận Không Giới Hạn Cảnh Báo", "PRO")}
                    className="px-2.5 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-bold text-xs rounded-lg transition shadow-sm cursor-pointer flex items-center gap-1 shrink-0"
                  >
                    <Crown className="w-3.5 h-3.5" />
                    <span>Nâng Cấp PRO</span>
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-900 h-2 rounded-full overflow-hidden border border-slate-800">
                <div
                  className={`h-full transition-all duration-500 ${
                    dailyUsage.count >= 5
                      ? "bg-rose-500"
                      : dailyUsage.count >= 3
                      ? "bg-amber-500"
                      : "bg-cyan-500"
                  }`}
                  style={{ width: `${Math.min(100, (dailyUsage.count / 5) * 100)}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-[10.5px] text-slate-400">
                <span>
                  {dailyUsage.count >= 5 ? (
                    <strong className="text-rose-400">Đã đạt tối đa 5/5 cảnh báo hôm nay.</strong>
                  ) : (
                    `Đã dùng ${dailyUsage.count}/5 cảnh báo hôm nay`
                  )}
                </span>
                <span className="text-cyan-400">
                  {Math.max(0, 5 - dailyUsage.count)} lượt còn lại (PRO không giới hạn)
                </span>
              </div>
            </div>
          )}

          {/* Alert Type Mode Segmented Tabs */}
          <div className="flex items-center gap-1.5 p-1 bg-slate-950 rounded-xl border border-slate-800">
            <button
              onClick={() => setAlertType("PRICE")}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                alertType === "PRICE"
                  ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 shadow-md shadow-amber-500/20"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
              }`}
            >
              <Target className="w-4 h-4" />
              <span>1. Cảnh Báo Mức Giá (Tự Cài)</span>
            </button>

            <button
              onClick={() => setAlertType("STRATEGY")}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                alertType === "STRATEGY"
                  ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-950 shadow-md shadow-cyan-500/20"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>2. Cảnh Báo Hệ Thống Giao Dịch</span>
            </button>
          </div>

          {/* TAB 1: CẢNH BÁO MỨC GIÁ */}
          {alertType === "PRICE" && (
            <form onSubmit={handleCreatePriceAlert} className="bg-slate-950/70 p-4 rounded-xl border border-slate-800/80 space-y-3.5 animate-fadeIn">
              <div className="flex items-center justify-between border-b border-slate-800/60 pb-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                  <Plus className="w-4 h-4 text-amber-400" />
                  Thiết lập cảnh báo giá chạm
                </h3>
                <span className="text-[11px] font-mono text-slate-400">
                  Giá hiện tại ({priceSymbol}): <strong className="text-emerald-400">{currentPrice || "---"}</strong>
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Symbol Select */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1 font-medium">Cặp tiền / Tài sản</label>
                  <select
                    value={priceSymbol}
                    onChange={(e) => setPriceSymbol(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-bold focus:outline-none focus:border-amber-400 cursor-pointer"
                  >
                    {symbols.length > 0 ? (
                      symbols.map((s) => (
                        <option key={s.symbol} value={s.symbol}>
                          {s.symbol} - {s.name}
                        </option>
                      ))
                    ) : (
                      <option value={currentSymbol}>{currentSymbol}</option>
                    )}
                  </select>
                </div>

                {/* Condition */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1 font-medium">Điều kiện kích hoạt</label>
                  <select
                    value={priceCondition}
                    onChange={(e) => setPriceCondition(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400 cursor-pointer"
                  >
                    <option value="PRICE_ABOVE">Giá Tăng Vượt &gt;=</option>
                    <option value="PRICE_BELOW">Giá Giảm Xuống &lt;=</option>
                  </select>
                </div>

                {/* Target Price */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] text-slate-400 font-medium">Mức giá mục tiêu</label>
                    {currentPrice && (
                      <button
                        type="button"
                        onClick={() => setTargetValue(currentPrice)}
                        className="text-[10px] text-cyan-400 hover:underline cursor-pointer"
                      >
                        Lấy giá hiện tại
                      </button>
                    )}
                  </div>
                  <input
                    type="number"
                    step="any"
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    placeholder={String(currentPrice || "2380.00")}
                    required
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-mono font-bold focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-[11px] text-slate-400 block mb-1 font-medium">Ghi chú nhắc nhở (Tùy chọn)</label>
                <input
                  type="text"
                  value={priceNote}
                  onChange={(e) => setPriceNote(e.target.value)}
                  placeholder="Ví dụ: Vùng cản H1, chạm là canh vào lệnh SELL..."
                  className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400"
                />
              </div>

              {/* Notification Options & Submit */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-800/60">
                <div className="flex items-center gap-4 text-xs text-slate-300">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={priceSoundAlarm}
                      onChange={(e) => setPriceSoundAlarm(e.target.checked)}
                      className="accent-amber-400 rounded"
                    />
                    <Volume2 className="w-3.5 h-3.5 text-amber-400" />
                    <span>Hú chuông âm thanh (3 lần)</span>
                  </label>

                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={priceSendTelegram}
                        onChange={(e) => setPriceSendTelegram(e.target.checked)}
                        className="accent-amber-400 rounded"
                      />
                      <Send className="w-3.5 h-3.5 text-sky-400" />
                      <span>Gửi Telegram</span>
                    </label>

                    {priceSendTelegram && (
                      user?.telegram_chat_id ? (
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-950/60 text-emerald-400 font-mono border border-emerald-800/60">
                          ID: {user.telegram_chat_id}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            setUserProfileModalOpen(true);
                          }}
                          className="text-[10.5px] text-sky-400 hover:underline cursor-pointer"
                        >
                          (Chưa cài Chat ID? Cài ngay)
                        </button>
                      )
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-bold text-xs rounded-lg transition shadow-md shadow-amber-500/20 cursor-pointer"
                >
                  {isSubmitting ? "Đang lưu..." : "+ Tạo Cảnh Báo Giá"}
                </button>
              </div>
            </form>
          )}

          {/* TAB 2: CẢNH BÁO HỆ THỐNG GIAO DỊCH */}
          {alertType === "STRATEGY" && (
            <form onSubmit={handleCreateStrategyAlert} className="bg-slate-950/70 p-4 rounded-xl border border-slate-800/80 space-y-3.5 animate-fadeIn">
              <div className="flex items-center justify-between border-b border-slate-800/60 pb-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-cyan-400" />
                  Bắn tín hiệu theo cài đặt cá nhân của hệ thống
                </h3>
                <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-950/80 text-cyan-300 font-mono border border-cyan-800/60">
                  Custom Strategy Engine
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Symbol Select */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1 font-medium">Cặp tiền</label>
                  <select
                    value={stratSymbol}
                    onChange={(e) => setStratSymbol(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-bold focus:outline-none focus:border-cyan-400 cursor-pointer"
                  >
                    {symbols.length > 0 ? (
                      symbols.map((s) => (
                        <option key={s.symbol} value={s.symbol}>
                          {s.symbol} - {s.name}
                        </option>
                      ))
                    ) : (
                      <option value={currentSymbol}>{currentSymbol}</option>
                    )}
                  </select>
                </div>

                {/* Timeframe Select */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1 font-medium">Khung thời gian</label>
                  <select
                    value={stratTimeframe}
                    onChange={(e) => setStratTimeframe(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-bold font-mono focus:outline-none focus:border-cyan-400 cursor-pointer"
                  >
                    {timeframes.map((tf) => (
                      <option key={tf} value={tf}>
                        Khung {tf}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Strategy System Select */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1 font-medium">Hệ thống giao dịch</label>
                  <select
                    value={stratName}
                    onChange={(e) => setStratName(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 font-bold focus:outline-none focus:border-cyan-400 cursor-pointer"
                  >
                    <option value="hma">HMA Breakout Pro (Zero Lag)</option>
                    <option value="smc">SMC Pro (MSB &amp; Liquidity)</option>
                    <option value="matrix">Matrix Strategy Pro</option>
                    <option value="bob_volman">Bob Volman Price Action Pro</option>
                    <option value="liquidity_sweep">Liquidity Sweep &amp; MSS Pro</option>
                    <option value="orb_session">ORB Session Breakout Pro</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Direction Filter */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1 font-medium">Hướng tín hiệu muốn nhận</label>
                  <select
                    value={stratDirection}
                    onChange={(e) => setStratDirection(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-400 cursor-pointer"
                  >
                    <option value="ALL">⚡ Tất cả tín hiệu (BUY &amp; SELL)</option>
                    <option value="BUY">↗ Chỉ nhận tín hiệu MUA (BUY Only)</option>
                    <option value="SELL">↘ Chỉ nhận tín hiệu BÁN (SELL Only)</option>
                  </select>
                </div>

                {/* Note */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1 font-medium">Ghi chú chiến lược (Tùy chọn)</label>
                  <input
                    type="text"
                    value={stratNote}
                    onChange={(e) => setStratNote(e.target.value)}
                    placeholder="Ví dụ: Theo dõi tài khoản cá nhân, đánh lệnh 0.5 lot..."
                    className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              {/* Personal Parameter Preset Information Badge */}
              <div className="p-3 rounded-xl bg-cyan-950/30 border border-cyan-500/30 flex items-start gap-2.5 text-xs text-cyan-200">
                <Sliders className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <div className="font-semibold text-slate-100 flex items-center gap-1.5">
                    <span>Đính kèm cấu hình riêng cho khung {stratTimeframe}:</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-cyan-900/60 text-cyan-300 font-mono">
                      Khung {stratTimeframe} Setup
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-300 leading-relaxed">
                    {stratName === "matrix" ? (
                      <>
                        EMA Nhanh/Chậm: <strong className="text-amber-300 font-mono">{strategyParams?.emaFast || 9}/{strategyParams?.emaSlow || 21}</strong> • RSI: <strong className="text-amber-300 font-mono">{strategyParams?.rsiPeriod || 14}</strong> • TP/SL: <strong className="text-emerald-400 font-mono">{strategyParams?.tpMult || 1.8}R/{strategyParams?.slMult || 1.2}R</strong> • Độ tin cậy: <strong className="text-cyan-300 font-mono">≥{strategyParams?.minConfidence || 35}%</strong>
                      </>
                    ) : stratName === "smc" ? (
                      <>
                        Vào lệnh: <strong className="text-amber-300 font-mono">{strategyParams?.entryMode || "Limit 1"}</strong> • Mid-Same: <strong className="text-amber-300 font-mono">{strategyParams?.midSamePct || 50}%</strong> • TP1: <strong className="text-emerald-400 font-mono">{strategyParams?.r1 || 1.0}R</strong> • TP2: <strong className="text-cyan-300 font-mono">{strategyParams?.r2 || 2.0}R</strong>
                      </>
                    ) : stratName === "bob_volman" ? (
                      <>
                        EMA: <strong className="text-amber-300 font-mono">{strategyParams?.volmanEmaPeriod || 24}</strong> • TP1: <strong className="text-emerald-400 font-mono">{strategyParams?.volmanR1 || 1.8}R</strong> • SL Buffer: <strong className="text-rose-400 font-mono">{strategyParams?.volmanSlBufferPips || 5}p</strong> • Setup: <strong className="text-cyan-300 font-mono">{[strategyParams?.volmanEnableBB && "BB", strategyParams?.volmanEnableTB && "TB"].filter(Boolean).join("+") || "Tất cả"}</strong>
                      </>
                    ) : stratName === "liquidity_sweep" ? (
                      <>
                        Vào lệnh: <strong className="text-amber-300 font-mono">{strategyParams?.sweepEntryMode || "RETEST"}</strong> • R:R: <strong className="text-emerald-400 font-mono">{strategyParams?.sweepRr || 2.0}R</strong> • SL Buffer: <strong className="text-rose-400 font-mono">{strategyParams?.sweepSlBufferPips || 1.5}p</strong> • Đỉnh/Đáy: <strong className="text-cyan-300 font-mono">{strategyParams?.sweepPeakLookback || 30} nến</strong>
                      </>
                    ) : stratName === "orb_session" ? (
                      <>
                        Phiên: <strong className="text-amber-300 font-mono">London/NY</strong> • R:R: <strong className="text-emerald-400 font-mono">{strategyParams?.orbRr || strategyParams?.rr || 3.0}R</strong> • Revert: <strong className="text-cyan-300 font-mono">{strategyParams?.orbRevertMode || "Smart"}</strong>
                      </>
                    ) : (
                      <>
                        Chu kỳ HMA: <strong className="text-amber-300 font-mono">{strategyParams?.period || 24}</strong> • TP1: <strong className="text-emerald-400 font-mono">{strategyParams?.r1 || 1.5}R</strong> • TP2: <strong className="text-cyan-300 font-mono">{strategyParams?.r2 || 2.0}R</strong> • Lọc dốc: <strong className="text-purple-300 font-mono">{strategyParams?.hmaSlopeFilter ? "Bật" : "Tắt"}</strong>
                      </>
                    )}
                    {strategyParams?.sessionFilterEnable && (
                      <span className="text-purple-300"> • Phiên: {strategyParams.sessionStart || "06:00"}-{strategyParams.sessionEnd || "03:30"}</span>
                    )}.
                  </div>
                </div>
              </div>

              {/* Notification Options & Submit */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-800/60">
                <div className="flex items-center gap-4 text-xs text-slate-300">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={stratSoundAlarm}
                      onChange={(e) => setStratSoundAlarm(e.target.checked)}
                      className="accent-cyan-400 rounded"
                    />
                    <Volume2 className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Hú chuông âm thanh (3 lần)</span>
                  </label>

                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={stratSendTelegram}
                        onChange={(e) => setStratSendTelegram(e.target.checked)}
                        className="accent-cyan-400 rounded"
                      />
                      <Send className="w-3.5 h-3.5 text-sky-400" />
                      <span>Gửi Telegram</span>
                    </label>

                    {stratSendTelegram && (
                      user?.telegram_chat_id ? (
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-950/60 text-emerald-400 font-mono border border-emerald-800/60">
                          ID: {user.telegram_chat_id}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            setUserProfileModalOpen(true);
                          }}
                          className="text-[10.5px] text-sky-400 hover:underline cursor-pointer"
                        >
                          (Chưa cài Chat ID? Cài ngay)
                        </button>
                      )
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-slate-950 font-bold text-xs rounded-lg transition shadow-md shadow-cyan-500/20 cursor-pointer"
                >
                  {isSubmitting ? "Đang lưu..." : "+ Tạo Cảnh Báo Hệ Thống"}
                </button>
              </div>
            </form>
          )}

          {/* Toast Notification */}
          {successMsg && (
            <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-500/50 text-emerald-300 text-xs font-bold flex items-center justify-between animate-fadeIn shadow-lg shadow-emerald-950/50">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{successMsg}</span>
              </div>
              <button 
                type="button" 
                onClick={() => setSuccessMsg("")}
                className="text-emerald-400/70 hover:text-emerald-300 text-xs"
              >
                ✕
              </button>
            </div>
          )}

          {/* Active Alerts List - Separated strictly by active tab */}
          <div>
            {alertType === "PRICE" ? (
              // TAB 1: DANH SÁCH CẢNH BÁO MỨC GIÁ
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                    <Target className="w-4 h-4 text-amber-400" />
                    <span>Danh Sách Cảnh Báo Mức Giá Đang Chờ Chạm ({priceAlerts.length})</span>
                  </h3>
                  <span className="text-[10px] text-slate-500 hidden sm:inline">
                    Tự động kích hoạt khi giá chạm mức đã cài
                  </span>
                </div>

                {priceAlerts.length > 0 ? (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {priceAlerts.map((al) => (
                      <div
                        key={al.id}
                        className="flex items-center justify-between p-3 rounded-xl border border-amber-900/40 bg-slate-950/80 hover:border-amber-700/60 text-xs transition"
                      >
                        <div className="flex items-center gap-3">
                          <span className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10.5px] font-bold flex items-center gap-1 shrink-0">
                            <Target className="w-3 h-3 text-amber-400" />
                            MỨC GIÁ
                          </span>

                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-extrabold text-slate-100 font-mono text-xs">{al.symbol}</span>

                            <span
                              className={`px-2 py-0.5 rounded font-mono text-xs font-bold border ${
                                al.condition === "PRICE_ABOVE"
                                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                                  : "bg-rose-500/15 text-rose-300 border-rose-500/30"
                              }`}
                            >
                              Giá chạm {al.condition === "PRICE_ABOVE" ? "≥" : "≤"} {al.target_value}
                            </span>

                            {al.note && (
                              <span className="text-slate-400 text-[11px] italic max-w-xs truncate">
                                "{al.note}"
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {al.sound_alarm && (
                            <Volume2 className="w-3.5 h-3.5 text-amber-400" title="Bật chuông âm thanh 3 lần" />
                          )}
                          {al.send_telegram && (
                            <Send className="w-3.5 h-3.5 text-sky-400" title="Gửi tin nhắn Telegram" />
                          )}

                          <button
                            onClick={() => handleDeleteAlert(al.id)}
                            className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition cursor-pointer ml-1"
                            title="Xóa cảnh báo này"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-slate-950/40 border border-slate-800/60 text-center text-xs text-slate-400 space-y-1">
                    <p className="font-semibold text-slate-300">Chưa có cảnh báo mức giá nào được cài đặt.</p>
                    <p className="text-[11px] text-slate-500">
                      Điền mức giá mục tiêu ở form bên trên và bấm nút vàng <strong>+ Tạo Cảnh Báo Giá</strong> để theo dõi.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // TAB 2: DANH SÁCH CẢNH BÁO HỆ THỐNG GIAO DỊCH
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-cyan-400" />
                    <span>Danh Sách Cảnh Báo Hệ Thống Đang Theo Dõi ({stratAlerts.length})</span>
                  </h3>
                  <span className="text-[10px] text-slate-500 hidden sm:inline">
                    Tự động quét và báo khi có tín hiệu BUY/SELL
                  </span>
                </div>

                {stratAlerts.length > 0 ? (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {stratAlerts.map((al) => (
                      <div
                        key={al.id}
                        className="flex items-center justify-between p-3 rounded-xl border border-cyan-900/40 bg-slate-950/80 hover:border-cyan-700/60 text-xs transition"
                      >
                        <div className="flex items-center gap-3">
                          <span className="px-2 py-0.5 rounded-md bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[10.5px] font-bold flex items-center gap-1 shrink-0">
                            <Zap className="w-3 h-3 text-cyan-400" />
                            TÍN HIỆU
                          </span>

                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-extrabold text-slate-100 font-mono text-xs">{al.symbol}</span>

                            <span className="text-[10px] font-mono text-cyan-300 font-bold px-1.5 py-0.2 rounded bg-cyan-950/80 border border-cyan-800/60">
                              Khung {al.timeframe || "M5"}
                            </span>

                            <span className="text-[11px] text-slate-300 font-medium">
                              • {getStrategyLabel(al.strategy)}
                            </span>

                            {al.direction && (
                              <span className={`text-[10px] font-bold font-mono px-1.5 py-0.2 rounded border ${
                                al.direction === "BUY"
                                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                  : al.direction === "SELL"
                                  ? "bg-rose-500/15 text-rose-400 border-rose-500/30"
                                  : "bg-slate-800 text-slate-300 border-slate-700"
                              }`}>
                                {al.direction === "BUY" ? "Chỉ BUY ↗" : al.direction === "SELL" ? "Chỉ SELL ↘" : "BUY & SELL"}
                              </span>
                            )}

                            {al.strategy_params && (
                              <span className="text-[10px] text-cyan-300 font-mono bg-cyan-950/60 px-1.5 py-0.2 rounded border border-cyan-800/50">
                                {al.strategy === "matrix"
                                  ? `EMA ${al.strategy_params.emaFast || al.strategy_params.ema_fast || 9}/${al.strategy_params.emaSlow || al.strategy_params.ema_slow || 21} • RSI ${al.strategy_params.rsiPeriod || al.strategy_params.rsi_period || 14} • TP ${al.strategy_params.tpMult || al.strategy_params.tp_mult || 1.8}R`
                                  : al.strategy === "smc"
                                  ? `${al.strategy_params.entryMode || al.strategy_params.entry_mode || "Limit 1"} • TP1 ${al.strategy_params.r1 || 1.0}R`
                                  : al.strategy === "bob_volman" || al.strategy === "volman"
                                  ? `EMA ${al.strategy_params.volmanEmaPeriod || 24} • TP1 ${al.strategy_params.volmanR1 || 1.8}R`
                                  : al.strategy === "liquidity_sweep" || al.strategy === "sweep" || al.strategy === "sweep_pro"
                                  ? `${al.strategy_params.sweepEntryMode || "RETEST"} • RR ${al.strategy_params.sweepRr || 2.0}R`
                                  : al.strategy === "orb_session" || al.strategy === "orb"
                                  ? `RR ${al.strategy_params.orbRr || al.strategy_params.rr || 3.0}R`
                                  : `HMA ${al.strategy_params.period || 24} • TP1 ${al.strategy_params.r1 || 1.5}R`}
                              </span>
                            )}

                            {al.note && (
                              <span className="text-slate-400 text-[11px] italic max-w-xs truncate">
                                "{al.note}"
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {al.sound_alarm && (
                            <Volume2 className="w-3.5 h-3.5 text-cyan-400" title="Bật chuông âm thanh 3 lần" />
                          )}
                          {al.send_telegram && (
                            <Send className="w-3.5 h-3.5 text-sky-400" title="Gửi tin nhắn Telegram" />
                          )}

                          <button
                            onClick={() => handleDeleteAlert(al.id)}
                            className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition cursor-pointer ml-1"
                            title="Xóa cảnh báo này"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-slate-950/40 border border-slate-800/60 text-center text-xs text-slate-400 space-y-1">
                    <p className="font-semibold text-slate-300">Chưa có cảnh báo hệ thống giao dịch nào.</p>
                    <p className="text-[11px] text-slate-500">
                      Chọn chiến lược ở form bên trên và bấm nút <strong>+ Tạo Cảnh Báo Hệ Thống</strong> để theo dõi.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Triggered Alert History */}
          {alertHistory && alertHistory.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5 flex items-center gap-1.5">
                <History className="w-3.5 h-3.5 text-slate-400" />
                <span>Lịch sử chuông báo đã kích hoạt ({alertHistory.length})</span>
              </h3>
              <div className="space-y-1.5 max-h-36 overflow-y-auto font-mono text-xs pr-1">
                {alertHistory.slice().reverse().map((ev, idx) => (
                  <div key={idx} className="p-2.5 rounded-xl bg-slate-950/50 border border-slate-800/60 text-slate-300">
                    <div className="flex items-center justify-between font-sans mb-1">
                      <span className="font-bold text-amber-400 flex items-center gap-1.5">
                        {ev.is_signal || ev.alert_type === "STRATEGY" ? (
                          <Zap className="w-3.5 h-3.5 text-cyan-400" />
                        ) : (
                          <Target className="w-3.5 h-3.5 text-amber-400" />
                        )}
                        <span>{ev.title}</span>
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {formatDisplayTime(ev.time, "local", "time")}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-sans">{ev.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}